import React from 'react';
import dayjs from 'dayjs';
// ImagePreview (click-to-zoom lightbox) has no kit equivalent yet — kept as the
// one documented @/components exception for this module.
import { ImagePreview } from '@/components';
import { FormModal } from '@/design-system/form-modal';
import { PackageMedia, PackageRow } from '@/services/api/packages';
import {
  ITEM_LEG_TYPE_LABELS,
  countDeliveryAttempts,
} from '@/services/api/status';
import { PackageConditionBadge, PackageStatusBadge } from './PackageBadges';
import PackageTimeline from './PackageTimeline';

const fmt = (v?: string | null) =>
  v ? dayjs(v).format('DD MMM YYYY, HH:mm') : '—';

// POD photos arrive grouped by `mediaType` (Dynamic Form per klien). Map known
// kinds to human labels; unknown types fall back to a humanized form so new
// kinds render without a code change. Order controls display sequence.
const MEDIA_TYPE_LABELS: Record<string, string> = {
  DELIVERY_PROOF: 'Foto Barang',
  DELIVERY_NOTE: 'Surat jalan',
};
const MEDIA_TYPE_ORDER = Object.keys(MEDIA_TYPE_LABELS);

const humanize = (type: string) =>
  MEDIA_TYPE_LABELS[type] ??
  type
    .toLowerCase()
    .split('_')
    .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
    .join(' ');

const groupMedia = (media: PackageMedia[]) => {
  const groups = new Map<string, PackageMedia[]>();
  for (const m of media) {
    if (!m?.url) continue;
    const bucket = groups.get(m.mediaType);
    if (bucket) bucket.push(m);
    else groups.set(m.mediaType, [m]);
  }
  return Array.from(groups.entries()).sort((a, b) => {
    const ia = MEDIA_TYPE_ORDER.indexOf(a[0]);
    const ib = MEDIA_TYPE_ORDER.indexOf(b[0]);
    return (ia === -1 ? 99 : ia) - (ib === -1 ? 99 : ib);
  });
};

const Section: React.FC<{
  title: React.ReactNode;
  children: React.ReactNode;
}> = ({ title, children }) => (
  <div className="border-b border-stroke-soft-200 px-6 py-5 last:border-b-0">
    <h3 className="mb-3.5 text-[11px] font-bold uppercase tracking-[0.08em] text-text-soft-400">
      {title}
    </h3>
    {children}
  </div>
);

const KV: React.FC<{ k: string; children: React.ReactNode }> = ({
  k,
  children,
}) => (
  <div className="flex items-start justify-between gap-4 py-1.5 text-[13px]">
    <span className="text-text-sub-600">{k}</span>
    <span className="text-right font-semibold text-text-strong-950">{children}</span>
  </div>
);

interface PackageDetailPanelProps {
  pkg: PackageRow | null;
  onClose: () => void;
}

const PackageDetailPanel: React.FC<PackageDetailPanelProps> = ({
  pkg,
  onClose,
}) => {
  const media = pkg?.media ?? [];
  const groups = groupMedia(media);
  const podTotal = groups.reduce((n, [, items]) => n + items.length, 0);

  const titleNode = pkg ? (
    <span className="flex min-w-0 flex-col gap-2">
      {/* Condition rides beside the status — v2 took damage off the status axis. */}
      <span className="flex flex-wrap items-center gap-1.5">
        <PackageStatusBadge status={pkg.status} />
        <PackageConditionBadge condition={pkg.condition} />
      </span>
      <span
        className="font-mono text-xl font-semibold tracking-tight text-text-strong-950"
        style={{ wordBreak: 'break-all' }}
      >
        {pkg.po}
      </span>
    </span>
  ) : (
    'Package'
  );

  return (
    <FormModal
      open={pkg !== null}
      onClose={onClose}
      position="right"
      size="md"
      padded={false}
      title={titleNode}
      subtitle={
        pkg?.bookingId ? (
          <span className="font-mono text-[11px] tabular-nums">
            Booking ID · {pkg.bookingId}
          </span>
        ) : undefined
      }
    >
      {pkg && (
        <div>
          {/* Package */}
          <Section title="Package">
            <KV k="PO / Item">
              <span className="font-mono">{pkg.po}</span>
            </KV>
            {pkg.itemName && pkg.itemName !== pkg.po && (
              <KV k="Item name">{pkg.itemName}</KV>
            )}
            <KV k="Waybill">
              <span className="font-mono">{pkg.deliveryId || '—'}</span>
            </KV>
            <KV k="Booking ID">
              <span className="font-mono">{pkg.bookingId || '—'}</span>
            </KV>
            <KV k="Recipient (outlet)">{pkg.outlet || '—'}</KV>
            <KV k="Sender">{pkg.sender || '—'}</KV>
            <KV k="Qty · berat">
              {pkg.qty} pcs · {pkg.weight} kg
            </KV>
            {/* Koli = dus fisik; beda dari `qty` yang menghitung unit barang. */}
            <KV k="Koli">{pkg.koli ?? 1} koli</KV>
            <KV k="Volume">
              {pkg.volume != null ? `${pkg.volume} m³` : '—'}
            </KV>
            {pkg.recipientAddress && (
              <KV k="Alamat penerima">
                <span className="font-normal text-text-sub-600">
                  {pkg.recipientAddress}
                </span>
              </KV>
            )}
            <KV k="Created">{fmt(pkg.createdAt)}</KV>
          </Section>

          {/* Retur — set only when the recipient refused the parcel. Reason code
              comes from the taxonomy; shown raw rather than fetching it. */}
          {(pkg.returReasonCode ||
            pkg.returReasonNote ||
            pkg.returWeight != null) && (
            <Section title="Retur">
              {pkg.returReasonCode && (
                <KV k="Alasan">
                  <span className="font-mono">{pkg.returReasonCode}</span>
                </KV>
              )}
              {pkg.returWeight != null && (
                <KV k="Berat retur">{pkg.returWeight} kg</KV>
              )}
              {pkg.returReasonNote && (
                <KV k="Catatan">
                  <span className="font-normal text-text-sub-600">
                    {pkg.returReasonNote}
                  </span>
                </KV>
              )}
            </Section>
          )}

          {/* Legs — one row per trip. Replaces the single delivery-uid column:
              a failed attempt keeps its leg, so attempts are countable and the
              first delivery's uid survives a retur. */}
          {pkg.legs && pkg.legs.length > 0 && (
            <Section
              title={
                <span>
                  Pengiriman{' '}
                  <span className="font-medium normal-case text-text-soft-400">
                    {countDeliveryAttempts(pkg.legs) > 1
                      ? ` · ${countDeliveryAttempts(pkg.legs)} percobaan`
                      : ''}
                  </span>
                </span>
              }
            >
              <ul className="space-y-1.5">
                {pkg.legs.map((leg) => (
                  <li
                    key={leg.id}
                    className="flex flex-wrap items-center gap-x-2 gap-y-0.5 text-[12px]"
                  >
                    <span className="font-mono tabular-nums text-text-soft-400">
                      #{leg.legSeq}
                    </span>
                    <span className="text-text-sub-600">
                      {ITEM_LEG_TYPE_LABELS[leg.legType] ?? leg.legType}
                    </span>
                    <span className="font-mono tabular-nums font-semibold text-text-strong-950">
                      {leg.deliveryUid}
                    </span>
                    {leg.deliveryStatus && (
                      <span className="font-mono text-[10px] text-text-sub-600">
                        {leg.deliveryStatus}
                      </span>
                    )}
                    {leg.isActive && (
                      <span className="rounded-full bg-(--theme-accent-alpha-10) px-1.5 py-0.5 text-[10px] font-semibold text-accent">
                        aktif
                      </span>
                    )}
                  </li>
                ))}
              </ul>
            </Section>
          )}

          {/* Proof of Delivery */}
          <Section
            title={
              <span>
                Proof of Delivery{' '}
                <span className="font-medium normal-case text-text-soft-400">
                  {podTotal ? ` · ${podTotal} foto` : ''}
                </span>
              </span>
            }
          >
            {groups.length === 0 ? (
              <div className="flex flex-col items-center gap-2 rounded-lg border border-dashed border-stroke-soft-200 py-8 text-text-soft-400">
                <i className="ri-image-line text-2xl" />
                <span className="text-sm">Belum ada Proof Of Delivery</span>
              </div>
            ) : (
              <>
                <div className="space-y-4">
                  {groups.map(([type, items]) => (
                    <div key={type}>
                      <div className="mb-1.5 flex items-center gap-2">
                        <span className="text-[13px] font-semibold text-text-strong-950">
                          {humanize(type)}
                        </span>
                        {items.length > 1 && (
                          <span className="inline-flex items-center rounded-full bg-bg-weak-50 px-2 py-0.5 text-[10px] font-semibold text-text-sub-600">
                            {items.length} foto
                          </span>
                        )}
                      </div>
                      <div className="flex flex-wrap gap-2">
                        {items.map((m, i) => (
                          <div key={m.id} className="w-[76px]">
                            <ImagePreview
                              src={m.url}
                              alt={`${humanize(type)} ${i + 1}`}
                              className="h-[76px] w-[76px] rounded-lg border border-stroke-soft-200 object-cover"
                            />
                            <div className="mt-1 truncate text-center text-[10px] text-text-soft-400">
                              {humanize(type)}
                              {items.length > 1 ? ` · ${i + 1}` : ''}
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  ))}
                </div>
                <div className="mt-3 flex items-start gap-2 rounded-lg bg-bg-weak-50 px-3 py-2.5 text-[11.5px] leading-snug text-text-sub-600">
                  <i className="ri-information-line mt-0.5 flex-shrink-0 text-sm text-text-soft-400" />
                  <span>
                    Jumlah &amp; jenis foto mengikuti{' '}
                    <b>Dynamic Form per klien</b>. <b>Foto barang</b> bisa lebih
                    dari satu; <b>Surat jalan/terima</b> hanya tampil bila
                    diunggah. Klik untuk perbesar.
                  </span>
                </div>
              </>
            )}
          </Section>

          {/* Timeline — item status history (intake → dispatch → delivery,
              branching to the retur legs). Read-only visibility. */}
          <Section title="Timeline">
            {pkg.timeline && pkg.timeline.length > 0 ? (
              <PackageTimeline events={pkg.timeline} />
            ) : (
              <div className="flex flex-col items-center gap-2 rounded-lg border border-dashed border-stroke-soft-200 py-8 text-text-soft-400">
                <i className="ri-history-line text-2xl" />
                <span className="text-sm">Belum ada riwayat status</span>
              </div>
            )}
          </Section>
        </div>
      )}
    </FormModal>
  );
};

export default PackageDetailPanel;
